import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Copy,
  Download,
  ExternalLink,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import MainLayout from '../../components/layout/MainLayout';
import SmartResponseSubNav from './SmartResponseSubNav';
import { dashboardApi } from '../../services/dashboardApi';
import {
  fetchSRCompetitionsAndBrands,
  fetchSRDashboard,
  fetchSRPortalLeads,
  fetchSRPortalStats,
  SRCompetition,
  SRPortalLead,
} from '../../services/smartResponseApi';

const PORTAL_CATEGORIES = [
  { key: 'dagligvarer', label: 'Dagligvarer & Mat' },
  { key: 'elektronikk', label: 'Elektronikk & Gadgets' },
  { key: 'reiser', label: 'Reiser & Ferie' },
  { key: 'hjem', label: 'Hjem, Bolig & Bil' },
  { key: 'mote', label: 'Mote, Sport & Skjønnhet' },
  { key: 'gavekort', label: 'SuperGavekort & Familie' },
] as const;

const PP_SUBID_LABELS: Record<string, { label: string; desc: string; badgeClass: string }> = {
  ppnoww: {
    label: 'ppnoww',
    desc: 'Website / Direkte / SEO (NO)',
    badgeClass: 'bg-teal-50 text-teal-800 border-teal-200',
  },
  ppnome: {
    label: 'ppnome',
    desc: 'Meta Ads Betalt (NO)',
    badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
  },
  ppnofb: {
    label: 'ppnofb',
    desc: 'Facebook Side Organisk (NO)',
    badgeClass: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  },
  ppnoem: {
    label: 'ppnoem',
    desc: 'E-mail / Nyhedsbrev (NO)',
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  ppnosm: {
    label: 'ppnosm',
    desc: 'SMS (NO)',
    badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  ppnoga: {
    label: 'ppnoga',
    desc: 'Google Ads (NO)',
    badgeClass: 'bg-rose-50 text-rose-800 border-rose-200',
  },
  ppdkww: {
    label: 'ppdkww',
    desc: 'Website / Direkte / SEO (DK)',
    badgeClass: 'bg-teal-50 text-teal-800 border-teal-200',
  },
  ppdkme: {
    label: 'ppdkme',
    desc: 'Meta Ads Betalt (DK)',
    badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
  },
  ppdkfb: {
    label: 'ppdkfb',
    desc: 'Facebook Side Organisk (DK)',
    badgeClass: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  },
  ppdkem: {
    label: 'ppdkem',
    desc: 'E-mail / Nyhedsbrev (DK)',
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
};

function resolveClientCategory(c: SRCompetition): string {
  const hay = `${c.comp_key || ''} ${c.title || ''} ${c.category || ''}`.toLowerCase();
  if (
    hay.includes('tui') ||
    hay.includes('ving') ||
    hay.includes('apollo') ||
    hay.includes('reise') ||
    hay.includes('rejse') ||
    hay.includes('travel')
  )
    return 'reiser';
  if (
    hay.includes('iphone') ||
    hay.includes('airpods') ||
    hay.includes('elkjop') ||
    hay.includes('elkjøp') ||
    hay.includes('garmin') ||
    hay.includes('polar') ||
    hay.includes('robotstovsuger') ||
    hay.includes('delonghi') ||
    hay.includes('kaffemaskin') ||
    hay.includes('iskrem') ||
    hay.includes('elsykkel') ||
    hay.includes('e-bike') ||
    hay.includes('allente') ||
    hay.includes('electronics')
  )
    return 'elektronikk';
  if (
    hay.includes('rema') ||
    hay.includes('coop') ||
    hay.includes('europris') ||
    hay.includes('supermarked') ||
    hay.includes('matbutikk') ||
    hay.includes('dagligvare') ||
    hay.includes('freia') ||
    hay.includes('milka') ||
    hay.includes('cocacola') ||
    hay.includes('coca-cola') ||
    hay.includes('burger_king') ||
    hay.includes('burger king') ||
    hay.includes('pizzabakeren') ||
    hay.includes('omo')
  )
    return 'dagligvarer';
  if (
    hay.includes('ikea') ||
    hay.includes('clas_ohlson') ||
    hay.includes('clas ohlson') ||
    hay.includes('biltema') ||
    hay.includes('jula') ||
    hay.includes('royaldesign') ||
    hay.includes('royal design') ||
    hay.includes('mummi') ||
    hay.includes('interior') ||
    hay.includes('interiør') ||
    hay.includes('nille') ||
    hay.includes('silvan') ||
    hay.includes('bensin') ||
    hay.includes('drivhus')
  )
    return 'hjem';
  if (
    hay.includes('hm_gavekort') ||
    hay.includes('h&m') ||
    hay.includes('zara') ||
    hay.includes('cubus') ||
    hay.includes('carlings') ||
    hay.includes('xxl') ||
    hay.includes('sport1') ||
    hay.includes('sport 1') ||
    hay.includes('fischer') ||
    hay.includes('skipakke') ||
    hay.includes('kicks') ||
    hay.includes('clarins') ||
    hay.includes('vita') ||
    hay.includes('lancome') ||
    hay.includes('lancôme') ||
    hay.includes('normal')
  )
    return 'mote';
  return 'gavekort';
}

function getCategoryLabel(c: SRCompetition): string {
  const key = resolveClientCategory(c);
  return PORTAL_CATEGORIES.find((p) => p.key === key)?.label || 'SuperGavekort & Familie';
}

// Proven top-converting competitions in Norway pinned at the very top:
// 1. REMA 1000 5.000 kr. (OneCall 114927 — 6 total conversions / 139.38 DKK)
// 2. Elsykkel 15.000 kr. (NorskeRabatter 114 — 33.3% CVR, 2 conversions / 52.32 DKK)
// 3. NORMAL 10.000 kr. (Spareklubb Norge 216 — 25.0% CVR, 1 conversion / 26.16 DKK)
// 4. Apple iPhone 17 (Spareklubb Norge 171 — 12.5% CVR, 1 conversion / 26.16 DKK)
const TOP_CONVERTING_PRIORITY_RANK: Record<string, number> = {
  no_onecall_rema1000_114927: 1,
  no_na_na_norskerabatter_elsykkel_114: 2,
  no_na_na_normal_10000_216: 3,
  no_na_na_iphone_17_171: 4,
};

// Low-CVR / strict-exclusion Talkmore (114903/114904) & duplicate OneCall flows pushed to the very bottom
const DEMOTED_LOW_CVR_KEYS = new Set<string>([
  'no_talkmore_gogift_114903',
  'no_talkmore_tui_114903',
  'no_talkmore_iskremmaskin_114903',
  'no_talkmore_babyutstyr_114903',
  'no_talkmore_gogift_meisel_114904',
  'no_talkmore_tui_meisel_114904',
  'no_talkmore_babyutstyr_meisel_114904',
  'no_onecall_gogift_114889',
  'no_onecall_ving_114874',
  'no_onecall_rema1000_orderfeed_141526',
]);

function sortCompetitionsByPayout(list: SRCompetition[], rotateAdvertisers: boolean): SRCompetition[] {
  const sorted = [...list].sort((a, b) => {
    const aDem = DEMOTED_LOW_CVR_KEYS.has(a.comp_key) ? 1 : 0;
    const bDem = DEMOTED_LOW_CVR_KEYS.has(b.comp_key) ? 1 : 0;
    if (aDem !== bDem) return aDem - bDem;

    const aRank = TOP_CONVERTING_PRIORITY_RANK[a.comp_key] ?? 9999;
    const bRank = TOP_CONVERTING_PRIORITY_RANK[b.comp_key] ?? 9999;
    if (aRank !== bRank) return aRank - bRank;

    const payDiff = Number(b.payout_dkk || 0) - Number(a.payout_dkk || 0);
    if (Math.abs(payDiff) > 0.001) return payDiff;
    const valDiff = Number(b.prize_value_dkk || 0) - Number(a.prize_value_dkk || 0);
    if (Math.abs(valDiff) > 0.001) return valDiff;
    return (b.start_date_iso || '').localeCompare(a.start_date_iso || '');
  });
  if (!rotateAdvertisers) return sorted;

  const pinnedTop = sorted.filter((c) => TOP_CONVERTING_PRIORITY_RANK[c.comp_key] !== undefined);
  const demotedBottom = sorted.filter((c) => DEMOTED_LOW_CVR_KEYS.has(c.comp_key));
  const middleItems = sorted.filter(
    (c) => TOP_CONVERTING_PRIORITY_RANK[c.comp_key] === undefined && !DEMOTED_LOW_CVR_KEYS.has(c.comp_key)
  );

  const pools = new Map<string, SRCompetition[]>();
  for (const item of middleItems) {
    const specificPool =
      (item.exclusion_pools || []).find((p) => p !== 'pool_global_blacklist') ||
      item.provider_network ||
      'default';
    if (!pools.has(specificPool)) pools.set(specificPool, []);
    pools.get(specificPool)!.push(item);
  }
  const queues = Array.from(pools.values()).sort(
    (a, b) => Number(b[0]?.payout_dkk || 0) - Number(a[0]?.payout_dkk || 0)
  );
  const interleaved: SRCompetition[] = [...pinnedTop];
  let added = true;
  while (added) {
    added = false;
    for (const q of queues) {
      if (q.length > 0) {
        interleaved.push(q.shift()!);
        added = true;
      }
    }
  }
  return [...interleaved, ...demotedBottom];
}

interface LifecycleEval {
  daysLeft: number;
  daysSinceStart: number;
  isExpired: boolean;
  inAll: boolean;
  inNew: boolean;
  inEndingSoon: boolean;
}

function evaluateClientLifecycle(competitions: SRCompetition[]): Record<string, LifecycleEval> {
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const raw: Record<
    string,
    {
      comp: SRCompetition;
      daysLeft: number;
      daysSinceStart: number;
      isExpired: boolean;
      startIso: string;
      endIso: string;
    }
  > = {};

  for (const c of competitions) {
    const startIso = c.start_date_iso || '2026-09-01';
    const endIso = c.end_date_iso || '2026-12-31';
    const startMs = new Date(startIso + 'T00:00:00Z').getTime();
    const endMs = new Date(endIso + 'T23:59:59Z').getTime();
    const daysLeft = Math.ceil((endMs - todayMs) / 86400000);
    const daysSinceStart = Math.max(0, Math.floor((todayMs - startMs) / 86400000));
    const isExpired = daysLeft < 0 || (c.status as string) === 'expired' || c.status === 'archived';
    raw[c.comp_key] = { comp: c, daysLeft, daysSinceStart, isExpired, startIso, endIso };
  }

  const activeList = Object.values(raw).filter((x) => !x.isExpired);

  const newKeys = new Set(
    activeList.filter((x) => x.daysSinceStart <= 14).map((x) => x.comp.comp_key)
  );
  if (newKeys.size < 3) {
    const sortedNewest = [...activeList].sort((a, b) => b.startIso.localeCompare(a.startIso));
    for (const item of sortedNewest.slice(0, 3)) {
      newKeys.add(item.comp.comp_key);
    }
  }

  const endingKeys = new Set(
    activeList.filter((x) => x.daysLeft <= 14).map((x) => x.comp.comp_key)
  );
  if (endingKeys.size < 1) {
    const sortedSoonest = [...activeList].sort((a, b) => a.endIso.localeCompare(b.endIso));
    for (const item of sortedSoonest.slice(0, 3)) {
      endingKeys.add(item.comp.comp_key);
    }
  }

  const result: Record<string, LifecycleEval> = {};
  for (const [key, item] of Object.entries(raw)) {
    result[key] = {
      daysLeft: item.daysLeft,
      daysSinceStart: item.daysSinceStart,
      isExpired: item.isExpired,
      inAll: !item.isExpired,
      inNew: !item.isExpired && newKeys.has(key),
      inEndingSoon: !item.isExpired && endingKeys.has(key),
    };
  }
  return result;
}

// Revenue Dashboard style MetricBox
const MetricBox = ({
  title,
  value,
  subLeftLabel,
  subLeftValue,
  subRightLabel,
  subRightValue,
  format = 'number',
  accentColor = 'text-gray-800',
}: {
  title: string;
  value: number | string;
  subLeftLabel: string;
  subLeftValue: number | string;
  subRightLabel: string;
  subRightValue: number | string;
  format?: 'number' | 'currency' | 'percent';
  accentColor?: string;
}) => {
  const formatVal = (v: number | string) => {
    if (v === '-' || v === undefined || v === null) return '-';
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(num)) return v;
    if (format === 'currency') {
      return `${num.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kr.`;
    }
    if (format === 'percent') {
      return `${num.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    }
    return Math.round(num).toLocaleString('da-DK');
  };

  return (
    <div className="bg-white p-3.5 flex flex-col justify-between h-28 relative overflow-hidden">
      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide text-center truncate px-1">
        {title}
      </div>
      <div className={`text-2xl font-bold ${accentColor} text-center my-auto tracking-tight`}>
        {formatVal(value)}
      </div>
      <div className="flex justify-between items-end text-[10px] text-gray-400 border-t border-gray-100 pt-1.5 px-0.5">
        <div className="text-left">
          <span className="block font-semibold text-gray-600">{formatVal(subLeftValue)}</span>
          <span>{subLeftLabel}</span>
        </div>
        <div className="text-right">
          <span className="block font-semibold text-gray-600">{formatVal(subRightValue)}</span>
          <span>{subRightLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default function SmartResponsePortalAdmin() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLeadsPage =
    location.pathname.startsWith('/premieportalen/leads') ||
    location.pathname.startsWith('/smartresponse/leads');

  const [chartDays, setChartDays] = useState<number>(14);

  const { data } = useQuery({
    queryKey: ['sr-competitions'],
    queryFn: () => fetchSRCompetitionsAndBrands('all'),
  });

  const { data: dashboardData, refetch: refetchSRDashboard } = useQuery({
    queryKey: ['sr-dashboard-kpis'],
    queryFn: () => fetchSRDashboard('all', 30),
    refetchInterval: 20000,
  });

  const { data: ppWctData, refetch: refetchPpWct } = useQuery({
    queryKey: ['pp-wct-revenue-dashboard', chartDays],
    queryFn: () => dashboardApi.getWctDashboard('all', 'pp', chartDays, false),
    refetchInterval: 30000,
  });

  const {
    data: portalLeadsData,
    refetch: refetchPortalLeads,
    isFetching: isFetchingLeads,
  } = useQuery({
    queryKey: ['sr-portal-leads'],
    queryFn: () => fetchSRPortalLeads(),
    refetchInterval: 15000,
  });

  const { data: portalStatsData, refetch: refetchPortalStats } = useQuery({
    queryKey: ['sr-portal-stats'],
    queryFn: () => fetchSRPortalStats(),
    refetchInterval: 15000,
  });

  const competitions = data?.competitions || [];
  const allPortalLeads: SRPortalLead[] = portalLeadsData?.leads || [];

  // Competition Filters
  const [selectedGeo, setSelectedGeo] = useState<'all' | 'no' | 'dk'>('all');
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBucket, setSelectedBucket] = useState<'all' | 'new' | 'ending_soon' | 'expired'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Lead Filters
  const [leadSearchQuery, setLeadSearchQuery] = useState<string>('');
  const [leadGeoFilter, setLeadGeoFilter] = useState<'all' | 'no' | 'dk'>('all');

  const handleCopyCloakedLink = (comp: SRCompetition) => {
    const slug = comp.cloak_slug || comp.comp_key;
    const fullUrl = `https://premieportal.com/go/${slug}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedSlug(comp.comp_key);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  // Filtered Competitions
  const geoFilteredCompetitions = competitions.filter((c) => {
    if (selectedGeo !== 'all' && c.geo !== selectedGeo) return false;
    if (selectedProvider !== 'all' && (c.provider_network || 'SmartResponse') !== selectedProvider)
      return false;
    if (selectedCategory !== 'all' && resolveClientCategory(c) !== selectedCategory) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const hay = `${c.title} ${c.cloak_slug || ''} ${c.provider_network || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const lifecycleMap = evaluateClientLifecycle(geoFilteredCompetitions);

  const filteredTableCompetitions = sortCompetitionsByPayout(
    geoFilteredCompetitions.filter((c) => {
      const ev = lifecycleMap[c.comp_key];
      if (!ev) return true;
      if (selectedBucket === 'all') return ev.inAll;
      if (selectedBucket === 'new') return ev.inNew;
      if (selectedBucket === 'ending_soon') return ev.inEndingSoon;
      if (selectedBucket === 'expired') return ev.isExpired;
      return true;
    }),
    selectedCategory === 'all' && selectedProvider === 'all'
  );

  // --- REAL HUMAN METRICS CALCULATION (TODAY, YESTERDAY, LAST 7 DAYS, SERIES) ---
  const analytics = useMemo(() => {
    const toNordicIso = (dInput: Date | string): string => {
      try {
        const dt = typeof dInput === 'string' ? new Date(dInput) : dInput;
        if (isNaN(dt.getTime())) return String(dInput).slice(0, 10);
        return dt.toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' });
      } catch {
        return typeof dInput === 'string' ? dInput.slice(0, 10) : dInput.toISOString().slice(0, 10);
      }
    };

    const now = new Date();
    const todayIso = toNordicIso(now);
    const yestDate = new Date(now.getTime() - 86400000);
    const yesterdayIso = toNordicIso(yestDate);

    const byDayMap = portalStatsData?.by_day || {};
    const wctCharts = ppWctData?.chart_data || [];
    const wctByDate: Record<string, { conversions: number; revenue: number; clicks: number }> = {};
    for (const row of wctCharts) {
      if (row && row.date) {
        const dKey = String(row.date).slice(0, 10);
        if (!wctByDate[dKey]) wctByDate[dKey] = { conversions: 0, revenue: 0, clicks: 0 };
        wctByDate[dKey].conversions += Number(row.conversions || 0);
        // wct_service returns row.commission in øre (cents) — convert to DKK
        wctByDate[dKey].revenue += Number(row.commission || 0) / 100;
      }
    }

    // SmartResponse Meta Instant Form leads & FeedOrder.aspx conversions grouped by Nordic date
    const srMetaLeads = dashboardData?.recent_leads || [];
    const srByDate: Record<
      string,
      { total: number; accepted: number; rejected: number; revenue: number }
    > = {};
    for (const ml of srMetaLeads) {
      const dKey = ml.created_at ? toNordicIso(ml.created_at) : todayIso;
      if (!srByDate[dKey]) {
        srByDate[dKey] = { total: 0, accepted: 0, rejected: 0, revenue: 0 };
      }
      srByDate[dKey].total += 1;
      const isAcc = ['accepted', 'live_accepted', 'dry_run_accepted'].includes(
        String(ml.sr_api_status || '')
      );
      if (isAcc) {
        srByDate[dKey].accepted += 1;
        srByDate[dKey].revenue +=
          Number(ml.payout_dkk || 0) + Number(ml.offerwall_revenue_dkk || 0);
      } else {
        srByDate[dKey].rejected += 1;
      }
    }

    // Meta Ad Insights totals (from act_995004339585761)
    const adWinners = dashboardData?.ad_winners || [];
    const metaAdClicks = adWinners.reduce((sum, a) => sum + Number(a.clicks || 0), 0);
    const metaAdImpressions = adWinners.reduce((sum, a) => sum + Number(a.impressions || 0), 0);
    const metaAdCtr = metaAdImpressions > 0 ? (metaAdClicks / metaAdImpressions) * 100 : 0;

    // Portal leads grouped by day
    const leadsByDate: Record<string, number> = {};
    for (const l of allPortalLeads) {
      const d = l.created_at ? toNordicIso(l.created_at) : String(l.last_active_at || '').slice(0, 10);
      if (d) leadsByDate[d] = (leadsByDate[d] || 0) + 1;
    }

    // Today stats (strictly real humans + real Meta/FeedOrder leads)
    const todayDayObj = byDayMap[todayIso] || portalStatsData?.today || {
      pageviews: 0,
      uniques: 0,
      clicks: 0,
    };
    const todayVisitors = Number(
      (todayDayObj as any).unique_visitors ?? (todayDayObj as any).uniques ?? 0
    );
    const todayPageviews = Number(todayDayObj.pageviews || 0);
    const todayClicks = Math.max(
      Number(todayDayObj.clicks || 0),
      Number(wctByDate[todayIso]?.clicks || 0)
    );
    const todayCtr = todayVisitors > 0 ? (todayClicks / todayVisitors) * 100 : 0;
    const todayAcceptedSr = Number(srByDate[todayIso]?.accepted || 0);
    const todayRejectedSr = Number(srByDate[todayIso]?.rejected || 0);
    const todayConversions = Math.max(
      Number(wctByDate[todayIso]?.conversions || 0),
      todayAcceptedSr
    );
    const todayRevenueDkk = Math.max(
      Number(wctByDate[todayIso]?.revenue || 0),
      Number(srByDate[todayIso]?.revenue || 0)
    );
    const todayLeads =
      Number(leadsByDate[todayIso] || 0) + Number(srByDate[todayIso]?.total || 0);

    // Yesterday stats
    const yestDayObj = byDayMap[yesterdayIso] || { pageviews: 0, uniques: 0, clicks: 0 };
    const yesterdayVisitors = Number(
      (yestDayObj as any).unique_visitors ?? (yestDayObj as any).uniques ?? 0
    );
    const yesterdayPageviews = Number(yestDayObj.pageviews || 0);
    const yesterdayClicks = Math.max(
      Number(yestDayObj.clicks || 0),
      Number(wctByDate[yesterdayIso]?.clicks || 0)
    );
    const yesterdayCtr = yesterdayVisitors > 0 ? (yesterdayClicks / yesterdayVisitors) * 100 : 0;
    const yesterdayConversions = Math.max(
      Number(wctByDate[yesterdayIso]?.conversions || 0),
      Number(srByDate[yesterdayIso]?.accepted || 0)
    );
    const yesterdayRevenueDkk = Math.max(
      Number(wctByDate[yesterdayIso]?.revenue || 0),
      Number(srByDate[yesterdayIso]?.revenue || 0)
    );
    const yesterdayLeads =
      Number(leadsByDate[yesterdayIso] || 0) + Number(srByDate[yesterdayIso]?.total || 0);

    // Last 7 days aggregation
    let weekVisitors = 0;
    let weekPageviews = 0;
    let weekClicks = 0;
    let weekConversions = 0;
    let weekRevenueDkk = 0;
    let weekLeads = 0;

    for (let i = 0; i < 7; i++) {
      const dIso = toNordicIso(new Date(now.getTime() - i * 86400000));
      const dStat = byDayMap[dIso];
      const dWct = wctByDate[dIso];
      const dSr = srByDate[dIso];
      const v =
        dIso === todayIso
          ? todayVisitors
          : Number((dStat as any)?.uniques ?? (dStat as any)?.unique_visitors ?? 0);
      const pv = dIso === todayIso ? todayPageviews : Number(dStat?.pageviews || 0);
      const cl =
        dIso === todayIso
          ? todayClicks
          : Math.max(Number(dStat?.clicks || 0), Number(dWct?.clicks || 0));
      const cv =
        dIso === todayIso
          ? todayConversions
          : Math.max(Number(dWct?.conversions || 0), Number(dSr?.accepted || 0));
      const rv =
        dIso === todayIso
          ? todayRevenueDkk
          : Math.max(Number(dWct?.revenue || 0), Number(dSr?.revenue || 0));
      const ld =
        dIso === todayIso
          ? todayLeads
          : Number(leadsByDate[dIso] || 0) + Number(dSr?.total || 0);

      weekVisitors += v;
      weekPageviews += pv;
      weekClicks += cl;
      weekConversions += cv;
      weekRevenueDkk += rv;
      weekLeads += ld;
    }

    const effectiveWeekClicks = weekClicks > 0 ? weekClicks : metaAdClicks;
    const weekCtr = weekVisitors > 0 ? (weekClicks / weekVisitors) * 100 : metaAdCtr;
    const weekCvr =
      effectiveWeekClicks > 0
        ? (weekConversions / effectiveWeekClicks) * 100
        : weekLeads > 0
        ? (weekConversions / weekLeads) * 100
        : 0;
    const weekEpc =
      effectiveWeekClicks > 0
        ? weekRevenueDkk / effectiveWeekClicks
        : weekLeads > 0
        ? weekRevenueDkk / weekLeads
        : 0;

    // Daily chart series for `chartDays`
    const series: {
      date: string;
      label: string;
      visitors: number;
      pageviews: number;
      clicks: number;
      conversions: number;
      revenue: number;
      leads: number;
    }[] = [];

    for (let i = chartDays - 1; i >= 0; i--) {
      const dObj = new Date(now.getTime() - i * 86400000);
      const dIso = toNordicIso(dObj);
      const dStat = byDayMap[dIso];
      const dWct = wctByDate[dIso];
      const dSr = srByDate[dIso];
      const visitors =
        dIso === todayIso
          ? todayVisitors
          : Number((dStat as any)?.uniques ?? (dStat as any)?.unique_visitors ?? 0);
      const pageviews = dIso === todayIso ? todayPageviews : Number(dStat?.pageviews || 0);
      const clicks =
        dIso === todayIso
          ? todayClicks
          : Math.max(Number(dStat?.clicks || 0), Number(dWct?.clicks || 0));
      const conversions =
        dIso === todayIso
          ? todayConversions
          : Math.max(Number(dWct?.conversions || 0), Number(dSr?.accepted || 0));
      const revenue =
        dIso === todayIso
          ? todayRevenueDkk
          : Math.max(Number(dWct?.revenue || 0), Number(dSr?.revenue || 0));
      const leads =
        dIso === todayIso
          ? todayLeads
          : Number(leadsByDate[dIso] || 0) + Number(dSr?.total || 0);

      series.push({
        date: dIso,
        label: dObj.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }),
        visitors,
        pageviews,
        clicks,
        conversions,
        revenue,
        leads,
      });
    }

    // All-time totals (human only + SmartResponse FeedOrder totals)
    const clicksByCompMap: Record<string, number> = portalStatsData?.clicks_by_comp || {};
    const edgeClicksSum = Math.max(
      Number(portalStatsData?.total_clicks || 0),
      Object.values(clicksByCompMap).reduce((a, b) => a + Number(b || 0), 0)
    );
    const allTimeVisitors = Math.max(Number(portalStatsData?.unique_visitors || 0), weekVisitors);
    const allTimeClicks = Math.max(edgeClicksSum, weekClicks);
    const allTimeConversions = Math.max(
      Number(ppWctData?.kpis?.total_conversions || 0),
      Number(dashboardData?.summary?.sr_api_accepted || 0),
      weekConversions
    );
    const allTimeRevenueDkk = Math.max(
      Number(ppWctData?.kpis?.total_revenue || 0) / 100,
      Number(dashboardData?.summary?.total_revenue_dkk || 0),
      weekRevenueDkk
    );
    const allTimeLeads = Math.max(
      allPortalLeads.length + Number(dashboardData?.summary?.raw_leads || srMetaLeads.length),
      weekLeads
    );

    return {
      today: {
        visitors: todayVisitors,
        pageviews: todayPageviews,
        clicks: todayClicks,
        ctr: todayCtr,
        conversions: todayConversions,
        revenue: todayRevenueDkk,
        leads: todayLeads,
        acceptedSr: todayAcceptedSr,
        rejectedSr: todayRejectedSr,
      },
      yesterday: {
        visitors: yesterdayVisitors,
        pageviews: yesterdayPageviews,
        clicks: yesterdayClicks,
        ctr: yesterdayCtr,
        conversions: yesterdayConversions,
        revenue: yesterdayRevenueDkk,
        leads: yesterdayLeads,
      },
      week: {
        visitors: weekVisitors,
        pageviews: weekPageviews,
        clicks: weekClicks,
        ctr: weekCtr,
        conversions: weekConversions,
        cvr: weekCvr,
        revenue: weekRevenueDkk,
        epc: weekEpc,
        leads: weekLeads,
      },
      allTime: {
        visitors: allTimeVisitors,
        clicks: allTimeClicks,
        conversions: allTimeConversions,
        revenue: allTimeRevenueDkk,
        leads: allTimeLeads,
      },
      metaAds: {
        clicks: metaAdClicks,
        impressions: metaAdImpressions,
        ctr: metaAdCtr,
        spendDkk: Number(dashboardData?.summary?.spend_dkk || 0),
      },
      clicksByCompMap,
      bySource: portalStatsData?.by_source || {},
      series,
    };
  }, [portalStatsData, ppWctData, dashboardData, allPortalLeads, chartDays]);

  // Filtered Leads for the dedicated Lead-opsamling tab
  const filteredPortalLeads = allPortalLeads.filter((l) => {
    if (leadGeoFilter !== 'all' && l.geo !== leadGeoFilter) return false;
    if (leadSearchQuery.trim()) {
      const q = leadSearchQuery.trim().toLowerCase();
      const compsStr = (l.interested_competitions || [])
        .map((c) => `${c.title} ${c.comp_key}`)
        .join(' ');
      const hay = `${l.name} ${l.email} ${l.source_comp_title || ''} ${compsStr}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const handleExportLeadsCsv = () => {
    const header = [
      'Navn',
      'Email',
      'Land',
      'Oplaast_Paa_Konkurrence',
      'Interesserede_Konkurrencer',
      'Antal_Klik',
      'Oprettet',
    ];
    const rows = filteredPortalLeads.map((l) => {
      const compTitles = (l.interested_competitions || [])
        .map((c) => c.title || c.comp_key)
        .join(' | ');
      return [
        `"${(l.name || '').replace(/"/g, '""')}"`,
        `"${(l.email || '').replace(/"/g, '""')}"`,
        `"${(l.geo || 'no').toUpperCase()}"`,
        `"${(l.source_comp_title || l.source_comp_key || '').replace(/"/g, '""')}"`,
        `"${compTitles.replace(/"/g, '""')}"`,
        String(l.total_clicks || (l.interested_competitions || []).length || 1),
        `"${l.created_at || ''}"`,
      ].join(',');
    });
    const csvContent = '\uFEFF' + [header.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `premieportalen_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeSourceEntries = Object.entries(analytics.bySource)
    .map(([code, rawVal]) => {
      const visits =
        typeof rawVal === 'number'
          ? Number(rawVal)
          : Number((rawVal as any)?.visits || 0);
      const clicks =
        typeof rawVal === 'number'
          ? 0
          : Number((rawVal as any)?.clicks || 0);
      return [code, { visits, clicks }] as const;
    })
    .sort((a, b) => b[1].clicks + b[1].visits - (a[1].clicks + a[1].visits));

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        <SmartResponseSubNav />

        {!isLeadsPage ? (
          <>
            {/* SEKTION 1: I DAG (KUN RIGTIGE MENNESKER) */}
            <div className="mb-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <ShieldCheck size={13} className="text-emerald-600" />
                    100% Rigtige Mennesker (Bots Frasorteret)
                  </span>
                  <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    I Dag — PremiePortalen Live
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    refetchPortalStats();
                    refetchSRDashboard();
                    refetchPpWct();
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-800 cursor-pointer"
                >
                  <RefreshCw size={12} />
                  Opdater live-tal
                </button>
              </div>

              <div className="rounded-sm shadow-sm border border-gray-200 bg-gray-200 gap-[1px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 overflow-hidden w-full">
                <MetricBox
                  title="Besøgende (I dag)"
                  value={analytics.today.visitors}
                  subLeftLabel="I går"
                  subLeftValue={analytics.yesterday.visitors}
                  subRightLabel="Sidevisninger"
                  subRightValue={analytics.today.pageviews}
                />
                <MetricBox
                  title="Klik /go/ (I dag)"
                  value={analytics.today.clicks}
                  subLeftLabel="I går"
                  subLeftValue={analytics.yesterday.clicks}
                  subRightLabel="Meta Ad Klik"
                  subRightValue={analytics.metaAds.clicks}
                  accentColor="text-[#007f8c]"
                />
                <MetricBox
                  title="CTR % (I dag)"
                  value={analytics.today.visitors > 0 ? analytics.today.ctr : analytics.metaAds.ctr}
                  subLeftLabel="Portal CTR"
                  subLeftValue={analytics.today.ctr}
                  subRightLabel="Meta CTR"
                  subRightValue={analytics.metaAds.ctr}
                  format="percent"
                />
                <MetricBox
                  title="Konverteringer (I dag)"
                  value={analytics.today.conversions}
                  subLeftLabel="I går"
                  subLeftValue={analytics.yesterday.conversions}
                  subRightLabel="SR Godkendt"
                  subRightValue={analytics.today.acceptedSr}
                />
                <MetricBox
                  title="Indtjening (I dag)"
                  value={analytics.today.revenue}
                  subLeftLabel="I går"
                  subLeftValue={analytics.yesterday.revenue}
                  subRightLabel="I dag"
                  subRightValue={analytics.today.revenue}
                  format="currency"
                  accentColor="text-emerald-700"
                />
                <MetricBox
                  title="Leads (I dag)"
                  value={analytics.today.leads}
                  subLeftLabel="SR Godkendt"
                  subLeftValue={analytics.today.acceptedSr}
                  subRightLabel="Afvist (Medlem)"
                  subRightValue={analytics.today.rejectedSr}
                />
              </div>
            </div>

            {/* SEKTION 2: DENNE UGE / SENESTE 7 DAGE (KUN RIGTIGE MENNESKER) */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Denne Uge (Seneste 7 Dage — Rigtige Mennesker)
                </h2>
                <span className="text-[11px] text-gray-500 font-medium">
                  SubID Struktur: <code className="font-mono text-gray-700">ppnoww</code> •{' '}
                  <code className="font-mono text-gray-700">ppnome</code> •{' '}
                  <code className="font-mono text-gray-700">ppnofb</code> •{' '}
                  <code className="font-mono text-gray-700">ppnoem</code>
                </span>
              </div>

              <div className="rounded-sm shadow-sm border border-gray-200 bg-gray-200 gap-[1px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 overflow-hidden w-full">
                <MetricBox
                  title="Besøgende (7 Dage)"
                  value={analytics.week.visitors}
                  subLeftLabel="Sidevisn. (7d)"
                  subLeftValue={analytics.week.pageviews}
                  subRightLabel="All-Time"
                  subRightValue={analytics.allTime.visitors}
                />
                <MetricBox
                  title="Klik /go/ (7 Dage)"
                  value={analytics.week.clicks}
                  subLeftLabel="Gns. / dag"
                  subLeftValue={analytics.week.clicks / 7}
                  subRightLabel="All-Time"
                  subRightValue={analytics.allTime.clicks}
                  accentColor="text-[#007f8c]"
                />
                <MetricBox
                  title="CTR % (7 Dage)"
                  value={analytics.week.ctr}
                  subLeftLabel="Klik (7d)"
                  subLeftValue={analytics.week.clicks}
                  subRightLabel="Besøg (7d)"
                  subRightValue={analytics.week.visitors}
                  format="percent"
                />
                <MetricBox
                  title="Konverteringer (7 Dage)"
                  value={analytics.week.conversions}
                  subLeftLabel="CVR %"
                  subLeftValue={`${analytics.week.cvr.toFixed(1)}%`}
                  subRightLabel="All-Time"
                  subRightValue={analytics.allTime.conversions}
                />
                <MetricBox
                  title="Indtjening (7 Dage)"
                  value={analytics.week.revenue}
                  subLeftLabel="EPC (kr/klik)"
                  subLeftValue={analytics.week.epc}
                  subRightLabel="All-Time"
                  subRightValue={analytics.allTime.revenue}
                  format="currency"
                  accentColor="text-emerald-700"
                />
                <MetricBox
                  title="Portal-Leads (7 Dage)"
                  value={analytics.week.leads}
                  subLeftLabel="7 Dage"
                  subLeftValue={analytics.week.leads}
                  subRightLabel="All-Time"
                  subRightValue={analytics.allTime.leads}
                />
              </div>
            </div>

            {/* SEKTION 3: GRAF MED UDVIKLINGEN (REVENUE DASHBOARD STIL) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 w-full">
              {/* Venstre Graf: Besøgende, Klik & Konverteringer */}
              <div className="bg-white p-4 sm:p-5 rounded-sm shadow-sm border border-gray-200 flex flex-col h-[320px] sm:h-[350px] min-w-0">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3">
                  <h3 className="text-sm font-bold text-gray-700">
                    Udvikling: Besøgende, Klik & Konverteringer
                  </h3>
                  <div className="inline-flex rounded border border-gray-200 bg-gray-50 p-0.5 text-[11px] font-semibold">
                    {[7, 14, 30].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setChartDays(d)}
                        className={`px-2 py-0.5 rounded cursor-pointer ${
                          chartDays === d
                            ? 'bg-[#007f8c] text-white'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {d}d
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 w-full min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={analytics.series}
                      margin={{ top: 5, right: 0, bottom: 5, left: -15 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                      <Bar
                        yAxisId="left"
                        dataKey="visitors"
                        name="Besøgende (Mennesker)"
                        fill="#007f8c"
                        barSize={16}
                        radius={[2, 2, 0, 0]}
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="clicks"
                        name="Klik (/go/)"
                        fill="#22c55e"
                        barSize={16}
                        radius={[2, 2, 0, 0]}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="conversions"
                        name="Konverteringer"
                        stroke="#f59e0b"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Højre Graf: Indtjening (DKK) & PP SubID Trafikkilder */}
              <div className="bg-white p-4 sm:p-5 rounded-sm shadow-sm border border-gray-200 flex flex-col h-[320px] sm:h-[350px] min-w-0">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3">
                  <h3 className="text-sm font-bold text-gray-700">
                    Indtjening (DKK) & PP SubID Trafikkilder
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {activeSourceEntries.length > 0 ? (
                      activeSourceEntries.slice(0, 4).map(([code, st]) => {
                        const meta = PP_SUBID_LABELS[code] || {
                          label: code,
                          desc: code,
                          badgeClass: 'bg-gray-100 text-gray-700 border-gray-200',
                        };
                        return (
                          <span
                            key={code}
                            title={`${meta.desc}: ${st.visits} besøg / ${st.clicks} klik`}
                            className={`px-2 py-0.5 rounded border text-[10px] font-bold ${meta.badgeClass}`}
                          >
                            {meta.label}: {st.visits} besøg • {st.clicks} klik
                          </span>
                        );
                      })
                    ) : (
                      <span className="px-2 py-0.5 rounded border bg-teal-50 text-teal-800 border-teal-200 text-[10px] font-bold">
                        ppnoww • ppnome • ppnofb aktiv
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 w-full min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={analytics.series}
                      margin={{ top: 5, right: 0, bottom: 5, left: -10 }}
                    >
                      <defs>
                        <linearGradient id="ppRevGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => `${v} kr`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="revenue"
                        name="Indtjening (DKK)"
                        stroke="#22c55e"
                        fillOpacity={1}
                        fill="url(#ppRevGrad)"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="clicks"
                        name="Klik (/go/)"
                        stroke="#0284c7"
                        strokeWidth={2}
                        dot={{ r: 2.5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* NEDENUNDER: Konkurrence-Overblik */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Top-bar over tabellen */}
              <div className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-gray-900">
                    Konkurrence-overblik ({filteredTableCompetitions.length})
                  </h2>
                  <select
                    value={selectedBucket}
                    onChange={(e) =>
                      setSelectedBucket(
                        e.target.value as 'all' | 'new' | 'ending_soon' | 'expired'
                      )
                    }
                    className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-800 cursor-pointer"
                  >
                    <option value="all">Status: Aktive konkurrencer</option>
                    <option value="new">Status: Nye konkurrencer</option>
                    <option value="ending_soon">Status: Slutter snart</option>
                    <option value="expired">Status: Udløbne</option>
                  </select>

                  <select
                    value={selectedGeo}
                    onChange={(e) => setSelectedGeo(e.target.value as 'all' | 'no' | 'dk')}
                    className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-800 cursor-pointer"
                  >
                    <option value="all">Land: Alle</option>
                    <option value="no">Land: Norge (NO)</option>
                    <option value="dk">Land: Danmark (DK)</option>
                  </select>
                </div>

                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="search"
                    placeholder="Søg konkurrence..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-1.5 text-xs text-gray-800 w-56"
                  />
                </div>
              </div>

              {/* Simpel Tabel med Dropdown-filtre direkte på baren over data */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold">
                      <th className="text-left py-3 px-4">Konkurrence</th>
                      <th className="text-left py-2.5 px-3">
                        <select
                          value={selectedProvider}
                          onChange={(e) => setSelectedProvider(e.target.value)}
                          aria-label="Filtrér efter udbyder"
                          className="bg-transparent font-semibold text-gray-700 hover:text-gray-900 cursor-pointer focus:outline-none"
                        >
                          <option value="all">Udbyder: Alle</option>
                          <option value="SmartResponse">Udbyder: SmartResponse</option>
                          <option value="Nordic Affiliates">Udbyder: Nordic Affiliates</option>
                        </select>
                      </th>
                      <th className="text-left py-2.5 px-3">
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          aria-label="Filtrér efter kategori"
                          className="bg-transparent font-semibold text-gray-700 hover:text-gray-900 cursor-pointer focus:outline-none"
                        >
                          <option value="all">Kategori: Alle</option>
                          {PORTAL_CATEGORIES.map((cat) => (
                            <option key={cat.key} value={cat.key}>
                              Kategori: {cat.label}
                            </option>
                          ))}
                        </select>
                      </th>
                      <th className="text-left py-3 px-3">Nedtælling</th>
                      <th className="text-right py-3 px-3">Klik (Mennesker)</th>
                      <th className="text-right py-3 px-3">Payout</th>
                      <th className="text-right py-3 px-3">Link</th>
                      <th className="text-right py-3 px-4">Meta</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTableCompetitions.map((c) => {
                      const ev = lifecycleMap[c.comp_key];
                      const slug = c.cloak_slug || compKeyFallback(c);
                      const isLiveMeta =
                        c.comp_key === 'no_onecall_rema1000_114927' ||
                        c.comp_key === 'no_talkmore_gogift_114903';

                      return (
                        <tr key={c.comp_key} className="hover:bg-gray-50">
                          {/* 1. Konkurrence */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-bold text-[10px]">
                                {c.geo.toUpperCase()}
                              </span>
                              <span className="font-semibold text-gray-900 text-sm">
                                {c.title}
                              </span>
                            </div>
                          </td>

                          {/* 2. Udbyder */}
                          <td className="py-3.5 px-3 text-gray-600 font-medium">
                            {c.provider_network || 'SmartResponse'}
                          </td>

                          {/* 3. Kategori */}
                          <td className="py-3.5 px-3 text-gray-600">
                            {getCategoryLabel(c)}
                          </td>

                          {/* 4. Nedtælling */}
                          <td className="py-3.5 px-3 whitespace-nowrap">
                            {ev?.isExpired ? (
                              <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 font-semibold text-[11px]">
                                Udløbet
                              </span>
                            ) : (
                              <span
                                className={`px-2 py-0.5 rounded font-semibold text-[11px] ${
                                  (ev?.daysLeft ?? 30) <= 14
                                    ? 'bg-amber-50 text-amber-800'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {ev?.daysLeft} dage tilbage
                              </span>
                            )}
                          </td>

                          {/* 5A. Klik (Kun rigtige mennesker fra portal_human_stats:v2) */}
                          <td className="py-3.5 px-3 text-right font-bold text-gray-800 whitespace-nowrap">
                            {Number(analytics.clicksByCompMap[c.comp_key] || 0).toLocaleString('da-DK')}
                          </td>

                          {/* 5. Payout */}
                          <td className="py-3.5 px-3 text-right font-bold text-emerald-700 text-sm whitespace-nowrap">
                            {c.payout_dkk} kr.
                          </td>

                          {/* 6. Link */}
                          <td className="py-3.5 px-3 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleCopyCloakedLink(c)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 text-gray-700 font-semibold cursor-pointer"
                                title={`https://premieportal.com/go/${slug}`}
                              >
                                <Copy size={12} />
                                {copiedSlug === c.comp_key ? 'Kopieret!' : 'Kopiér'}
                              </button>
                              <a
                                href={`https://premieportal.com/go/${slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
                              >
                                Åbn <ExternalLink size={11} />
                              </a>
                            </div>
                          </td>

                          {/* 7. Meta */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-2">
                              {isLiveMeta && (
                                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-bold">
                                  Klar i Meta
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/premieportalen/campaigns?comp_key=${encodeURIComponent(
                                      c.comp_key
                                    )}`
                                  )
                                }
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold cursor-pointer"
                              >
                                <Rocket size={11} />
                                Meta Ad
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          /* EGEN FANE: LEAD-OPSAMLING (/premieportalen/leads) — LIVE META & FEEDORDER.ASPX LEADS + PORTAL LEADS */
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-200">
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    Meta Instant Form & SmartResponse Livefeed Leads ({(dashboardData?.recent_leads || []).length})
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Alle leads fra Meta Form <code>1548544003699895</code> sendes automatisk til{' '}
                    <code>FeedOrder.aspx (sourceId=100168, partnerId=1336, productId=114927)</code> med unikt dummy-data i baggrunden.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => refetchSRDashboard()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold cursor-pointer"
                >
                  <RefreshCw size={13} />
                  Opdater Livefeed
                </button>
              </div>

              {(dashboardData?.recent_leads || []).length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-xs text-gray-500">
                  Ingen Meta Instant Form leads modtaget endnu.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold">
                        <th className="text-left py-3 px-3">Tidspunkt</th>
                        <th className="text-left py-3 px-3">Navn & Dummy-mail (148)</th>
                        <th className="text-left py-3 px-3">Tlf (117) & Født (119)</th>
                        <th className="text-left py-3 px-3">Baggrunds-adresse (150/151)</th>
                        <th className="text-left py-3 px-3">Operatør (7753)</th>
                        <th className="text-left py-3 px-3">FeedOrder.aspx Livefeed Status</th>
                        <th className="text-right py-3 px-3">Payout</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(dashboardData?.recent_leads || []).map((ml) => {
                        const ofp = (ml.orderfeed_params || {}) as Record<string, any>;
                        const fParams = (ofp.feedorder_params || {}) as Record<string, any>;
                        const fullName = `${ofp.firstname || fParams['522'] || ''} ${ofp.lastname || fParams['523'] || ''}`.trim() || ml.leadgen_id;
                        const emailStr = ofp.email || fParams['148'] || '—';
                        const phoneStr = ofp.phone || fParams['117'] || '—';
                        const dobStr = ofp.dob || fParams['119'] || '—';
                        const addrStr = fParams['150'] ? `${fParams['150']}, ${fParams['151'] || ''}` : '—';
                        const opCode = ofp.operator_code_7753 || fParams['7753'] || '';
                        const isAccepted = ['accepted', 'live_accepted', 'dry_run_accepted'].includes(String(ml.sr_api_status || ''));

                        return (
                          <tr key={ml.leadgen_id} className="hover:bg-gray-50 align-top">
                            <td className="py-3 px-3 whitespace-nowrap text-gray-600">
                              {ml.created_at
                                ? new Date(ml.created_at).toLocaleString('da-DK', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'}
                              <div className="text-[10px] text-gray-400 font-mono">{ml.leadgen_id}</div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="font-bold text-gray-900">{fullName}</div>
                              <div className="text-[11px] text-gray-500 font-mono">{emailStr}</div>
                            </td>
                            <td className="py-3 px-3 whitespace-nowrap">
                              <div className="font-semibold text-gray-800">{phoneStr}</div>
                              <div className="text-[11px] text-gray-500">Født: {dobStr} ({ml.age_bucket} år)</div>
                            </td>
                            <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                              {addrStr}
                            </td>
                            <td className="py-3 px-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-0.5 rounded font-bold text-[11px] ${
                                  isAccepted
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                    : 'bg-amber-50 text-amber-800 border border-amber-200'
                                }`}
                              >
                                {ml.current_operator || 'Ukendt'} {opCode ? `(7753=${opCode})` : ''}
                              </span>
                            </td>
                            <td className="py-3 px-3 max-w-md">
                              {isAccepted ? (
                                <div>
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 font-bold text-[11px]">
                                    ✓ Godkendt i Livefeed (&lt;result&gt;Success&lt;/result&gt;)
                                  </span>
                                  <p className="text-[11px] text-gray-500 mt-1">
                                    Sendt til FeedOrder.aspx og accepteret af SmartResponse / OneCall.
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200 font-bold text-[11px]">
                                    Sendt til Livefeed — Afvist af OneCall betingelse
                                  </span>
                                  <p className="text-[11px] text-gray-600 mt-1">
                                    {ml.disqualify_reason || 'Afvist af Product Conditions (7753!=492/6427/342)'}
                                  </p>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right font-bold whitespace-nowrap">
                              <span className={isAccepted ? 'text-emerald-700 text-sm' : 'text-gray-400'}>
                                {Number(ml.payout_dkk || 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-bold text-gray-900">
                  Indsamlede Leads ({filteredPortalLeads.length})
                </h2>
                <select
                  value={leadGeoFilter}
                  onChange={(e) => setLeadGeoFilter(e.target.value as 'all' | 'no' | 'dk')}
                  className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-800 cursor-pointer"
                >
                  <option value="all">Land: Alle ({allPortalLeads.length})</option>
                  <option value="no">
                    Land: Norge ({allPortalLeads.filter((l) => l.geo === 'no').length})
                  </option>
                  <option value="dk">
                    Land: Danmark ({allPortalLeads.filter((l) => l.geo === 'dk').length})
                  </option>
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="search"
                    placeholder="Søg navn, e-mail eller konkurrence..."
                    value={leadSearchQuery}
                    onChange={(e) => setLeadSearchQuery(e.target.value)}
                    className="rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-1.5 text-xs text-gray-800 w-64"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => refetchPortalLeads()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold cursor-pointer"
                >
                  <RefreshCw size={13} className={isFetchingLeads ? 'animate-spin' : ''} />
                  Opdater
                </button>

                <button
                  type="button"
                  onClick={handleExportLeadsCsv}
                  disabled={filteredPortalLeads.length === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold cursor-pointer"
                >
                  <Download size={13} />
                  Download CSV
                </button>
              </div>
            </div>

            {filteredPortalLeads.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
                <p className="text-sm font-semibold text-gray-800">Ingen leads endnu</p>
                <p className="text-xs text-gray-500 mt-1">
                  Når en bruger på premieportal.com udfylder navn og e-mail ved 4. konkurrence-klik,
                  vises de automatisk her.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-gray-600 font-semibold">
                      <th className="text-left py-3 px-4">Navn</th>
                      <th className="text-left py-3 px-3">E-mail</th>
                      <th className="text-left py-3 px-3">Land</th>
                      <th className="text-left py-3 px-3">Konkurrencer med interesse</th>
                      <th className="text-right py-3 px-4">Dato</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPortalLeads.map((lead) => {
                      const interested = lead.interested_competitions || [];
                      return (
                        <tr key={lead.lead_id || lead.email} className="hover:bg-gray-50 align-top">
                          <td className="py-3.5 px-4 font-bold text-gray-900 text-sm">
                            {lead.name}
                          </td>
                          <td className="py-3.5 px-3 font-medium text-emerald-700">
                            {lead.email}
                          </td>
                          <td className="py-3.5 px-3">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-bold text-[10px]">
                              {(lead.geo || 'no').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3.5 px-3 max-w-md">
                            <div className="flex flex-wrap gap-1.5">
                              {interested.length > 0 ? (
                                interested.map((ic) => (
                                  <span
                                    key={ic.comp_key}
                                    className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-200 text-[11px] font-medium"
                                  >
                                    {ic.title || ic.comp_key}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-600">
                                  {lead.source_comp_title || lead.source_comp_key || '—'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4 text-right text-gray-500 whitespace-nowrap">
                            {lead.last_active_at
                              ? new Date(lead.last_active_at).toLocaleString('da-DK')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

function compKeyFallback(c: SRCompetition): string {
  return c.comp_key || '';
}
